import { NextApiRequest, NextApiResponse } from 'next'
import { AuthError } from '@auth0/nextjs-auth0'
import type { WithApiAuthRequiredPageRoute } from '@auth0/nextjs-auth0'
import { sanitizeHeaderString } from '@reward-platform/utils'
import { getPlatformBasePath } from '~/utils/platform'
import {
  AUTH_COOKIES,
  getBaseUrlFromServer,
  getCookiesFromRequest,
  NextRequestParameter,
  serializeCookie,
  setCookieInResponse,
} from '../utils'
import { getAccessToken, getAuth0Server } from './auth0.server'
import { handleApiRouteError, NextAuth0Error } from '../errors'
import { logRequestToDatadog } from '../logger'
import {
  serializeSilentLoginReturnToCookie,
  getSanitizedReturnTo,
  serializeSilentLoginCookie,
  serializeAuth0ClearSessionCookies,
} from './auth0.utils'
import { getPartnerAuth0Config } from './auth0.config'

export type Auth0Server = ReturnType<typeof getAuth0Server>

export const getLogoutHandler =
  (auth0Server: Auth0Server) => async (req: NextApiRequest, res: NextApiResponse) => {
    const requestCookies = getCookiesFromRequest(req)
    const serializedClearedSessionCookies = serializeAuth0ClearSessionCookies(requestCookies)
    setCookieInResponse(res, serializedClearedSessionCookies)

    try {
      const returnTo = getBaseUrlFromServer(req)
      await auth0Server.handleLogout(req, res, { returnTo })
    } catch (error) {
      handleApiRouteError(error, req, res)
    }
  }

export const getCallbackHandler =
  (auth0Server: Auth0Server) => async (req: NextApiRequest, res: NextApiResponse) => {
    try {
      const { config } = getPartnerAuth0Config(req)
      const requestCookies = getCookiesFromRequest(req)
      const silentLoginReturnToCookie = requestCookies[AUTH_COOKIES.SILENT_LOGIN_RETURN_TO]
      const hasDoneSilentLogin = !!silentLoginReturnToCookie
      const userIsNotLoggedIn = req.query.error === 'login_required'
      const rawReturnTo = sanitizeHeaderString(silentLoginReturnToCookie)
      const validReturnTo =
        (rawReturnTo &&
          getSanitizedReturnTo(rawReturnTo, config.authorizationParams.redirect_uri)) ??
        '/'

      const serializedResponseCookies: string[] = []

      if (hasDoneSilentLogin) {
        serializedResponseCookies.push(serializeSilentLoginCookie(true))
        serializedResponseCookies.push(serializeSilentLoginReturnToCookie(validReturnTo, false))
      }

      if (hasDoneSilentLogin && userIsNotLoggedIn) {
        // Fails silently, clears session cookies and return to page
        const serializedClearedSessionCookies = serializeAuth0ClearSessionCookies(requestCookies)
        setCookieInResponse(res, [...serializedResponseCookies, ...serializedClearedSessionCookies])
        res.redirect(validReturnTo)
      } else {
        if (serializedResponseCookies.length) {
          setCookieInResponse(res, serializedResponseCookies)
        }
        await auth0Server.handleCallback(req, res)
      }
    } catch (error) {
      if (error instanceof AuthError) {
        const authError = new NextAuth0Error(error)
        logRequestToDatadog(req, res, authError)

        // Could be worth catching this error cookie in getInitialProps and handle it in the client
        const serializesAuthErrorCookie = serializeCookie(
          AUTH_COOKIES.AUTH_ERROR_CODE,
          error.code,
          { path: getPlatformBasePath() || '/' }
        )
        setCookieInResponse(res, serializesAuthErrorCookie)

        const logoutHandler = getLogoutHandler(auth0Server)
        await logoutHandler(req, res)
      }
    }
  }

export const getSilentLoginHandler =
  (auth0Server: Auth0Server) => async (req: NextApiRequest, res: NextApiResponse) => {
    const { config } = getPartnerAuth0Config(req)
    const rawReturnTo = sanitizeHeaderString(req.query.returnTo)
    const validReturnTo =
      (rawReturnTo && getSanitizedReturnTo(rawReturnTo, config.authorizationParams.redirect_uri)) ??
      '/'

    try {
      setCookieInResponse(res, [serializeSilentLoginReturnToCookie(validReturnTo, true)])

      await auth0Server.handleLogin(req, res, {
        authorizationParams: { prompt: 'none' },
        returnTo: validReturnTo,
      })
    } catch (error) {
      handleApiRouteError(error, req, res)
    }
  }

export const getAuth0Handler = (req: NextRequestParameter) => {
  const auth0Server = getAuth0Server(req)
  return auth0Server.handleAuth({
    callback: getCallbackHandler(auth0Server),
    'silent-login': getSilentLoginHandler(auth0Server),
    logout: getLogoutHandler(auth0Server),
  })
}

export const withApiAuthRequired: WithApiAuthRequiredPageRoute = (apiRoute) => async (req, res) => {
  try {
    const { accessToken } = await getAccessToken(req, res)

    req.headers.authorization = `Bearer ${accessToken}`

    // TODO: could validate the token here similar to the middleware in bff
  } catch (error) {
    handleApiRouteError(error, req, res)
  }

  return apiRoute(req, res)
}
